import { createSelector } from 'reselect'

import type { StoreState } from '~/store/store'

// ── Types ────────────────────────────────────────────────────────────────────

export type KeyValue = string | number | boolean | null | undefined

type ComparisonOp<V extends string | number | boolean = string | number | boolean> = {
    gte?: V
    lte?: V
    gt?: V
    lt?: V
    eq?: V
    in?: readonly V[]
    notIn?: readonly V[]
    isNull?: boolean
    isUndefined?: boolean
    isNullish?: boolean
}

type AttrFilter<V> = [V] extends [string | number | boolean | null] ? V | ComparisonOp<Extract<V, string | number | boolean>> : never
type ScalarOf<V> = Extract<V, string | number | boolean | null>

type ExtractChild<T> = T extends readonly (infer C)[] ? C : NonNullable<T>

/**
 * Module-augmentable registry mapping each entity name to its typed relations.
 * The generated selectors file augments this interface so the recursive
 * `IncludeMap` / `WithRelations` types can resolve nested includes by name.
 *
 * Each relation is encoded as `{ value: <return type>; entity: <child entity name> }`:
 *   - `value` is the runtime return type of the relation accessor (array for
 *     hasMany / hasManyThrough, optional singleton for hasOne).
 *   - `entity` is the string-literal key into `EntityRelations` for the child
 *     entity, used to recursively look up the next include layer's allowed keys.
 *
 * Empty by default so this file has no value-level dependency on the generated
 * file; declaration merging fills it in from the generated module.
 */
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface EntityRelations {}

/**
 * Relation predicates are intentionally restricted to the *atomic* shape
 * (no top-level `OR` / `AND` / `NOT`). Lifting the boolean ops into relation
 * children would require an exists-style sub-query engine; v1 keeps things
 * simple — boolean composition is a top-level-only privilege.
 */
export type RelationFilter<Child = unknown> = SelectByAttrsAtomic<ExtractChild<Child>> & {
    any?: SelectByAttrsAtomic<ExtractChild<Child>>
    none?: SelectByAttrsAtomic<ExtractChild<Child>>
}

/**
 * Closed mapped type for the *atomic* slice of a query: scalar attrs accept
 * value-or-ComparisonOp, and the optional `Relations` parameter (emitted by
 * the generator from declared `hasMany` relations) adds typed
 * `{ any | none }` predicate slots. No string-index escape hatch.
 *
 * This shape is used for relation predicates and as the base of the
 * top-level `SelectByAttrs` (which adds `OR` / `AND` / `NOT`).
 *
 * The `{}` default is intentional: `keyof {} = never`, so the relations branch
 * collapses to `{}`. Both `Record<string, never>` and `Record<string, unknown>`
 * have `keyof = string`, which would re-introduce an index signature and accept
 * arbitrary relation keys. Hence the eslint suppression.
 */
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export type SelectByAttrsAtomic<T, Relations extends Record<string, unknown> = {}> = {
    [K in keyof T as [ScalarOf<T[K]>] extends [never] ? never : K]?: AttrFilter<ScalarOf<T[K]>>
} & {
    [K in keyof Relations]?: RelationFilter<Relations[K]>
}

/**
 * Top-level query shape: the atomic slice plus Prisma-style boolean
 * composition. `OR` is the deduped union of arm matches (empty array →
 * vacuously false), `AND` is the intersection (empty array → vacuously true),
 * `NOT` excludes the inner predicate's matches. All three are recursive into
 * `SelectByAttrs<T>` so arms can themselves contain boolean ops.
 *
 * Bool ops are top-level only by design (see `RelationFilter` above).
 */
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export type SelectByAttrs<T, Relations extends Record<string, unknown> = {}> = SelectByAttrsAtomic<T, Relations> & {
    OR?: ReadonlyArray<SelectByAttrs<T, Relations>>
    AND?: ReadonlyArray<SelectByAttrs<T, Relations>>
    NOT?: SelectByAttrs<T, Relations>
}

// ── OrderBy Types ────────────────────────────────────────────────────────────

export type OrderByDirection = 'asc' | 'desc'

/**
 * orderBy entry: an attribute name (asc by default), a tuple `[attr, direction]`,
 * or a custom comparator function.
 */
export type OrderByEntry<T> = keyof T | readonly [keyof T, OrderByDirection] | ((a: T, b: T) => number)

export type OrderByOption<T> = ReadonlyArray<OrderByEntry<T>>

/**
 * Per-relation node inside a nested include. Either `true` (stitch but don't
 * recurse) or a nested `IncludeMap<CE>` over the child entity's own relations,
 * additionally allowing the reserved keys `where` (filter children) and
 * `orderBy` (sort children).
 *
 * `where` / `orderBy` are reserved AT THIS NESTED LEVEL ONLY. The top-level
 * `IncludeMap<E>` doesn't accept them — the parent's `where` is the second
 * positional `attrs` arg and the parent's `orderBy` lives on `SelectByOptions`.
 *
 * The conditional split keeps the leaf shape (child entity with no declared
 * relations) usable: intersecting `IncludeMap<CE>` with the reserved keys
 * would otherwise collapse to `Record<string, never> & { where?: ...; orderBy?: ... }`,
 * which TypeScript reduces to a type where neither key is assignable
 * (`never & SelectByAttrs<...> = never`).
 */
type IncludeReserved<CE extends string, CT> = {
    where?: SelectByAttrs<CT, RelationsAsReturnTypes<CE>>
    orderBy?: OrderByOption<CT>
}

export type IncludeNode<CE extends string, CT> = true | (CE extends keyof EntityRelations ? IncludeMap<CE> & IncludeReserved<CE, CT> : IncludeReserved<CE, CT>)

/**
 * Recursive Prisma-style include map. The shape is bounded by the entity's
 * schema-declared relations: each known relation key accepts an `IncludeNode`
 * whose child-entity is taken from `EntityRelations[E][K].entity`.
 *
 * Children of relations whose target has no schema-defined relations resolve
 * to `Record<string, never>`, so the only nested values they accept are `true`
 * or an empty object (plus the reserved `where`/`orderBy` keys) — driving the
 * negative type tests.
 */
export type IncludeMap<E extends string> = E extends keyof EntityRelations
    ? {
          [K in keyof EntityRelations[E]]?: EntityRelations[E][K] extends { value: infer V; entity: infer CE extends string }
              ? IncludeNode<CE, [V] extends [readonly (infer C)[]] ? C : NonNullable<V>>
              : never
      }
    : Record<string, never>

/**
 * Recursive return-shape transformer: walks an `IncludeMap` over an entity's
 * `EntityRelations` entry and emits `Omit<T, includedKey> & { includedKey:
 * <child shape> }` at every level. Singular (hasOne) relations preserve their
 * `| undefined` tail; plural (hasMany / hasManyThrough) relations stay
 * `readonly`.
 *
 * The body distributes over `T` via the naked `T extends unknown` conditional
 * so that when `T` is a discriminated union (e.g. `Surgery = ScheduledSurgery |
 * UnScheduledSurgery`) each arm runs through `Omit<arm, K> & { … }` separately,
 * preserving its own discriminants (`bookingId`, `plannedTimes`, `id`, …).
 * Without distribution, `Omit<A | B, K>` collapses to keys common to both arms,
 * and `Include<Surgery, X>` would no longer be a structural subtype of
 * `Surgery` — breaking helpers typed against bare `Surgery` (see §10 in
 * `docs/plans/local/shallow-selectors-documentation.md`).
 *
 * `[Inc] extends [...]` (rather than the unwrapped form) deliberately keeps the
 * Inc-side conditional from distributing — relevant when callers forward an
 * `as const` literal whose include map narrows to specific keys. Only `T`
 * distributes; `Inc` stays atomic.
 */
export type WithRelations<T, E extends string, Inc> = [Inc] extends [true | undefined]
    ? T & _RelationsBrand<E>
    : [Inc] extends [Record<string, unknown>]
      ? E extends keyof EntityRelations
          ? T extends unknown
              ? (Omit<T, Extract<keyof Inc, keyof EntityRelations[E]>> & {
                    [K in keyof Inc & keyof EntityRelations[E]]: EntityRelations[E][K] extends {
                        value: infer V
                        entity: infer CE extends string
                    }
                        ? // `[V] extends [...]` and `[undefined] extends [V]`: tuple-wrap V
                          // because hasOne relations are typed `Child | undefined` and we don't
                          // want either guard to split into `WithRelations<Child> | WithRelations<undefined>`.
                          [V] extends [readonly (infer C)[]]
                            ? WithRelations<C, CE, Inc[K]>[]
                            : [undefined] extends [V]
                              ? WithRelations<NonNullable<V>, CE, Inc[K]> | undefined
                              : WithRelations<V, CE, Inc[K]>
                        : never
                }) &
                    _RelationsBrand<E>
              : never
          : T & _RelationsBrand<E>
      : T & _RelationsBrand<E>

export type SelectByOptions<T, E extends string = string> = {
    orderBy?: OrderByOption<T>
    include?: IncludeMap<E>
}

// ── GroupBy / KeyBy Types ─────────────────────────────────────────────────────

/** Scalar-only keys of `T` (mirrors the key filter in `SelectByAttrsAtomic`). */
type ScalarKeyOf<T> = keyof {
    [K in keyof T as [ScalarOf<T[K]>] extends [never] ? never : K]: 0
}

type GroupByKeyFn<T> = (item: T) => KeyValue

/** Composite attribute spec: a non-empty tuple of scalar attribute names. */
type CompositeGroupBySpec<T> = readonly [ScalarKeyOf<T>, ...ScalarKeyOf<T>[]]

/**
 * Spec for `groupBy` / `keyBy`. One of:
 *   - a single scalar attribute name (`'status_code'`),
 *   - a composite (non-empty) tuple of scalar attribute names (joined into a string key),
 *   - a key function `(item) => KeyValue` (cached by reference identity), or
 *   - a `[keyFn, depKey]` pair where `depKey` makes the function cacheable by
 *     a stable string instead of by reference (mirrors `orderBy` comparators).
 */
export type GroupBySpec<T> = ScalarKeyOf<T> | CompositeGroupBySpec<T> | GroupByKeyFn<T> | readonly [GroupByKeyFn<T>, string]

/**
 * Grouping clauses are mutually exclusive (`keyBy` is the "last wins" special
 * case of `groupBy`). They live in the same options object as `include` /
 * `orderBy` and are only accepted by the plural selectors.
 */
type GroupingOptions<T> = {
    groupBy?: GroupBySpec<T>
    keyBy?: GroupBySpec<T>
}

export type SelectByPluralOptions<T, E extends string = string> = SelectByOptions<T, E> & GroupingOptions<T>

/**
 * Unified Mode B query object (Prisma-style). Top level accepts only reserved
 * keys — field predicates and boolean ops live under `where`.
 */
export type StormQuery<T, E extends string = string> = {
    where?: SelectByAttrs<T, RelationsAsReturnTypes<E>>
    include?: IncludeMap<E>
    orderBy?: OrderByOption<T>
} & GroupingOptions<T> & {
        /** Forbid entity predicates / boolean ops at the top level — they belong in `where`. */
        [K in keyof SelectByAttrs<T, RelationsAsReturnTypes<E>>]?: never
    }

/** Mode B on singular selectors — `where` is required to identify the row. */
export type StormQuerySingular<T, E extends string = string> = {
    where: SelectByAttrs<T, RelationsAsReturnTypes<E>>
    include?: IncludeMap<E>
    orderBy?: OrderByOption<T>
    groupBy?: never
    keyBy?: never
} & {
    [K in keyof SelectByAttrs<T, RelationsAsReturnTypes<E>>]?: never
}

/** First-argument union for plural hooks / selectors (Mode A bare where | Mode B). */
export type StormQueryArg<T, E extends string = string> = SelectByAttrs<T, RelationsAsReturnTypes<E>> | StormQuery<T, E>

/** First-argument union for singular hooks / selectors. */
export type StormQuerySingularArg<T, E extends string = string> = SelectByAttrs<T, RelationsAsReturnTypes<E>> | StormQuerySingular<T, E>

/** Mode B query object accepted by a generated plural `use*` hook. */
export type StormQueryOfPlural<H> = H extends UsePluralFn<infer T, infer E> ? StormQuery<T, E> : never

/** Mode B query object accepted by a generated singular `use*` hook. */
export type StormQueryOfSingular<H> = H extends UseSingularFn<infer T, infer E> ? StormQuerySingular<T, E> : never

const STORM_QUERY_KEYS = ['where', 'include', 'orderBy', 'groupBy', 'keyBy'] as const

type StormQueryRecord<T, E extends string> = {
    where?: SelectByAttrs<T, RelationsAsReturnTypes<E>>
} & SelectByPluralOptions<T, E>

/** True when `obj` carries any reserved Storm query key (Mode B). */
export function isStormQuery<T, E extends string = string>(obj: Record<string, unknown>): obj is StormQueryRecord<T, E> & Record<string, unknown> {
    return STORM_QUERY_KEYS.some(k => k in obj)
}

function _peelStormQueryOpts<T, E extends string>(query: SelectByPluralOptions<T, E>): SelectByPluralOptions<T, E> | undefined {
    if (query.include !== undefined || query.orderBy !== undefined || query.groupBy !== undefined || query.keyBy !== undefined) {
        // Return the original query so its reference is stable across repeat calls,
        // letting the WeakMap fast path (`existingWeak.opts === callOptions`) hit.
        // The downstream cache-key builder only reads option keys; a leaked `where`
        // key is never read, so results and cache keys are unaffected.
        return query
    }
    return undefined
}

/**
 * Maps Mode A / Mode B call shapes to `{ attrs, opts }` before `_runByQuery`.
 *
 * Mode detection: any reserved top-level key (`where`, `include`, `orderBy`,
 * `groupBy`, `keyBy`) triggers Mode B. Otherwise arg1 is the bare filter (Mode A)
 * and options live on the optional second argument.
 *
 * Mode B ignores `arg2` at runtime (the type system rejects a second argument).
 * Missing `where` in Mode B means no filter (`attrs = {}`).
 *
 * Plural weak-cache keys use normalized `attrs` when non-empty; for include-only /
 * orderBy-only Mode B calls (`attrs = {}`) the original query object is kept so
 * repeat calls with the same object reference hit the WeakMap fast path.
 */
export function _normalizeStormArgs<T, E extends string = string>(
    arg1?: Record<string, unknown>,
    arg2?: SelectByPluralOptions<T, E>
): { attrs: Record<string, unknown>; opts: SelectByPluralOptions<T, E> | undefined } {
    if (arg1 === undefined) {
        return { attrs: {}, opts: arg2 }
    }

    if (isStormQuery<T, E>(arg1)) {
        for (const key of Object.keys(arg1)) {
            if (!STORM_QUERY_KEYS.includes(key as (typeof STORM_QUERY_KEYS)[number]) && arg1[key] !== undefined) {
                throw new Error(
                    `Storm query (Mode B) carries an unexpected top-level key "${key}". Boolean operators (OR/AND/NOT) and field predicates must live inside \`where\`, not alongside reserved keys (${STORM_QUERY_KEYS.join(', ')}).`
                )
            }
        }
        return {
            attrs: (arg1.where as Record<string, unknown> | undefined) ?? {},
            opts: _peelStormQueryOpts(arg1),
        }
    }

    return { attrs: arg1, opts: arg2 }
}

/** Non-null scalar projection of a key/function-return type. */
type ScalarKeyType<X> = NonNullable<X> & (string | number | boolean)

/**
 * Resolve the precise `Map` key type from a `GroupBySpec`. Checked in order:
 * `[fn, depKey]` tuple → fn return; bare fn → fn return; composite tuple →
 * `string` (joined); single attr name → that field's scalar type.
 */
type GroupKey<Row, S> = S extends readonly [(item: never) => infer K, string]
    ? ScalarKeyType<K>
    : S extends (item: never) => infer K
      ? ScalarKeyType<K>
      : S extends readonly (keyof Row)[]
        ? string
        : S extends keyof Row
          ? ScalarKeyType<Row[S]>
          : string | number | boolean

/**
 * Plural selector return shape. A `keyBy` clause yields `Map<Key, Row>`, a
 * `groupBy` clause yields `Map<Key, Row[]>`, and the absence of both keeps the
 * plain `Row[]`. The `infer S` matches a *required* clause key, so the broad
 * (non-literal) options type — whose `groupBy` / `keyBy` are optional — falls
 * through to the array shape, matching the `IncludeOf` pattern.
 */
export type PluralResult<Row, Opts> = Opts extends { keyBy: infer S }
    ? [S] extends [undefined]
        ? Row[]
        : Map<GroupKey<Row, S>, Row>
    : Opts extends { groupBy: infer S }
      ? [S] extends [undefined]
          ? Row[]
          : Map<GroupKey<Row, S>, Row[]>
      : Row[]

// ── Relation Brand + Public `Include<T, Inc>` ───────────────────────────────

export const RELATIONS_BRAND = Symbol('RELATIONS_BRAND')

export type _RelationsBrand<E extends string> = { readonly [RELATIONS_BRAND]?: E }

export const DENORMALIZER_BRAND = Symbol('DENORMALIZER_BRAND')

export type _DenormalizerBrand<E extends string> = { readonly [DENORMALIZER_BRAND]: E }

/**
 * Brands an unbranded, structurally-valid entity value as denormalized.
 *
 * Denormalizer entity types carry a required phantom `DENORMALIZER_BRAND`. That
 * brand is what stops a Storm value (or a bare object literal) from being passed
 * where a Denormalizer value is expected. When you must legitimately build a
 * Denormalizer value by hand - e.g. a test fixture - wrap the literal with this
 * helper. It supplies the phantom brand via `never` (the bottom type, assignable
 * to every concrete entity brand), so the call site stays cast-free while the
 * structural shape is still fully checked at the assignment.
 */
export const asDenormalized = <T>(value: T): T & _DenormalizerBrand<never> => value as T & _DenormalizerBrand<never>

/**
 * Recovers the entity name string from a branded entity type.
 */
export type _EntityOf<T> = T extends _RelationsBrand<infer E> ? (E extends string ? E : never) : never

/**
 * Hydrates an entity type with the requested relation slots using the deep
 * include map semantics. The returned type has the same scalar fields as `T`
 * plus each requested relation populated.
 *
 * The brand is preserved, so subsequent lookups still resolve and `Include`
 * can be nested.
 */
export type Include<T, Inc extends IncludeMap<_EntityOf<T>>> = WithRelations<T, _EntityOf<T>, Inc>

// ── Relation Accessor Types ──────────────────────────────────────────────────

type PerStateRelationAccessor = (parent: never) => unknown
type RelationAccessor = (state: StoreState) => PerStateRelationAccessor
/**
 * One relation entry: an accessor that returns a per-row child fetcher, plus
 * an optional `childRelations` thunk pointing at the child entity's own
 * relations config. The thunk defers resolution so cyclic schemas (e.g.
 * `departments → locations → departments`) don't trigger TDZ errors.
 */
type RelationEntry = {
    accessor: RelationAccessor
    childRelations?: () => RelationsConfig | undefined
}
type RelationsConfig = Record<string, RelationEntry>

/**
 * Project a runtime relations config into the relations-as-return-types map
 * consumed by `SelectByAttrsAtomic` for typed relation predicates (`{ any: ... }`,
 * implicit attrs, etc.). Type-only counterpart to the schema's
 * `EntityRelations[E]` value-type projection.
 */
export type RelationsAsReturnTypes<E extends string> = E extends keyof EntityRelations
    ? { [K in keyof EntityRelations[E]]: EntityRelations[E][K] extends { value: infer V } ? V : never }
    : // eslint-disable-next-line @typescript-eslint/no-empty-object-type
      {}

/**
 * Extract `Opts['include']` for the factory return type. `Opts` is the call
 * site's literal options object (typically inferred from `as const`).
 *
 * The pattern matches a *required* `include` so that the optional shape on
 * `SelectByOptions` (`include?: IncludeMap<E>`) — which is what unbound `Opts`
 * defaults to when no `opts` arg is passed — falls through to `true`. That
 * lets `WithRelations` return the unstitched `T` for those calls. Literal
 * call-site objects do supply `include:` as required and infer `I` directly.
 */
export type IncludeOf<Opts> = Opts extends { include: infer I } ? I : true

// ── Test-only Metrics ────────────────────────────────────────────────────────

/**
 * Module-private metric counters used by the caching test suite. Always on:
 * incrementing a number is a few-cycle operation, so the overhead is negligible
 * compared to the index lookups and predicate work, and removing a feature flag
 * keeps the hot path branch-free.
 *
 * `cacheHits`/`cacheMisses` track the per-`FactoryCache` result cache wired up
 * in `_runByQuery`: a hit returns the previously-stored array reference without
 * re-running `.filter`; a miss recomputes and stores a fresh entry.
 */
const _metrics = {
    pipelineRuns: 0,
    filterCalls: 0,
    cacheHits: 0,
    cacheMisses: 0,
    factoryRebuilds: 0,
}

export function _resetSelectorMetrics(): void {
    _metrics.pipelineRuns = 0
    _metrics.filterCalls = 0
    _metrics.cacheHits = 0
    _metrics.cacheMisses = 0
    _metrics.factoryRebuilds = 0
}

export function _getSelectorMetrics(): Readonly<typeof _metrics> {
    return { ..._metrics }
}

// ── Indexing Helpers ────────────────────────────────────────────────────────

export function _groupBy<T>(items: readonly T[], keyFn: (item: T) => KeyValue): Map<string | number | boolean, T[]> {
    const index = new Map<string | number | boolean, T[]>()
    for (const item of items) {
        const key = keyFn(item)
        if (key === null || key === undefined) continue
        let group = index.get(key)
        if (!group) {
            group = []
            index.set(key, group)
        }
        group.push(item)
    }
    return index
}

export function _keyBy<T>(items: readonly T[], keyFn: (item: T) => KeyValue): Map<string | number | boolean, T> {
    const index = new Map<string | number | boolean, T>()
    for (const item of items) {
        const key = keyFn(item)
        if (key !== null && key !== undefined) index.set(key, item)
    }
    return index
}

/** Build composite key for multi-attr index/lookup – avoids per-item array allocations. */
function _compositeKey(keys: readonly string[], vals: Record<string, string | number | boolean | undefined>): string {
    const k0 = keys[0] as string
    if (keys.length === 2) return String(vals[k0] ?? '') + ' :: ' + String(vals[keys[1] as string] ?? '')
    let out = String(vals[k0] ?? '')
    for (let i = 1; i < keys.length; i++) out += ' :: ' + String(vals[keys[i] as string] ?? '')
    return out
}

// ── Comparison Helpers ───────────────────────────────────────────────────────

function _isComparisonOp(v: unknown): v is ComparisonOp {
    if (typeof v !== 'object' || v === null || Array.isArray(v)) return false
    return (
        'gte' in v || 'lte' in v || 'gt' in v || 'lt' in v || 'eq' in v || 'in' in v || 'notIn' in v || 'isNull' in v || 'isUndefined' in v || 'isNullish' in v
    )
}

/** Top-level predicate: null/undefined attribute values never match any operator. */
function _matchesComparison(val: KeyValue, op: ComparisonOp): boolean {
    const o = op as Record<string, KeyValue | readonly (string | number | boolean)[] | undefined>
    if (o.isNullish !== undefined) {
        if (o.isNullish !== (val === null || val === undefined)) return false
    }
    if (o.isNull !== undefined) {
        if (o.isNull !== (val === null)) return false
    }
    if (o.isUndefined !== undefined) {
        if (o.isUndefined !== (val === undefined)) return false
    }
    if (val === null || val === undefined) {
        if (
            o.eq !== undefined ||
            o.gte !== undefined ||
            o.lte !== undefined ||
            o.gt !== undefined ||
            o.lt !== undefined ||
            o.in !== undefined ||
            o.notIn !== undefined
        ) {
            return false
        }
        return true
    }
    if (o.eq !== undefined && val !== o.eq) return false
    if (o.gte !== undefined && (val as string | number | boolean) < (o.gte as string | number | boolean)) return false
    if (o.lte !== undefined && (val as string | number | boolean) > (o.lte as string | number | boolean)) return false
    if (o.gt !== undefined && (val as string | number | boolean) <= (o.gt as string | number | boolean)) return false
    if (o.lt !== undefined && (val as string | number | boolean) >= (o.lt as string | number | boolean)) return false
    if (Array.isArray(o.in) && !(o.in as readonly (string | number | boolean)[]).includes(val as string | number | boolean)) return false
    if (Array.isArray(o.notIn) && (o.notIn as readonly (string | number | boolean)[]).includes(val as string | number | boolean)) return false
    return true
}

/**
 * Canonical null-bound rule for relation predicates: a null/undefined child
 * attribute is treated as ±∞ for interval ops (`gte`/`lte`/`gt`/`lt`), so a
 * `null` end_time matches `{ gte: anything }`. `eq`/`in` still require a
 * concrete value (so `null` never matches them); `notIn` admits `null` because
 * `null` is not a member of any value list.
 */
function _matchesRelationComparison(val: KeyValue, op: ComparisonOp): boolean {
    if (val === null || val === undefined) {
        const o = op as Record<string, unknown>
        if (o.isNullish !== undefined && o.isNullish === false) return false
        if (o.isNull !== undefined && o.isNull !== (val === null)) return false
        if (o.isUndefined !== undefined && o.isUndefined !== (val === undefined)) return false
        if (o.eq !== undefined) return false
        if (o.in !== undefined) return false
        return true
    }
    return _matchesComparison(val, op)
}

/** Predicate over a relation child – uses the relation null-bound rule above. */
function _matchesRelationChild<T>(child: T, attrs: Record<string, unknown>): boolean {
    const obj = child as unknown as Record<string, unknown>
    for (const k of Object.keys(attrs)) {
        const v = attrs[k]
        if (v === undefined) continue
        const itemVal = obj[k] as KeyValue
        if (_isComparisonOp(v)) {
            if (!_matchesRelationComparison(itemVal, v)) return false
        } else if (itemVal !== v) {
            return false
        }
    }
    return true
}

// ── OrderBy Helpers ──────────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
const _fnCache = new WeakMap<Function, number>()
let _fnIdCounter = 0
// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
function _getFnId(fn: Function): number {
    let id = _fnCache.get(fn)
    if (id === undefined) {
        id = ++_fnIdCounter
        _fnCache.set(fn, id)
    }
    return id
}

function _serializeOrderBy<T>(orderBy: OrderByOption<T> | undefined): string {
    if (!orderBy || orderBy.length === 0) return '∅'
    const parts: string[] = []
    for (const e of orderBy) {
        if (typeof e === 'function') parts.push('fn:' + _getFnId(e))
        else if (Array.isArray(e)) parts.push(`t:${String(e[0])}:${String(e[1])}`)
        else parts.push(`s:${String(e)}`)
    }
    return parts.join('|')
}

function _compareScalar(av: unknown, bv: unknown): number {
    if (Object.is(av, bv)) return 0
    if (av === null || av === undefined) return 1
    if (bv === null || bv === undefined) return -1
    return (av as string | number) < (bv as string | number) ? -1 : 1
}

function _makeComparator<T>(orderBy: OrderByOption<T>): (a: T, b: T) => number {
    const cmps: Array<(a: T, b: T) => number> = []
    for (const e of orderBy) {
        if (typeof e === 'function') {
            cmps.push(e as (a: T, b: T) => number)
        } else if (Array.isArray(e)) {
            const key = e[0] as keyof T
            const sign: 1 | -1 = (e as readonly [keyof T, OrderByDirection])[1] === 'desc' ? -1 : 1
            cmps.push((a, b) => _compareScalar(a[key], b[key]) * sign)
        } else {
            const key = e as keyof T
            cmps.push((a, b) => _compareScalar(a[key], b[key]))
        }
    }
    return (a, b) => {
        for (const cmp of cmps) {
            const r = cmp(a, b)
            if (r !== 0) return r
        }
        return 0
    }
}

function _sortIfNeeded<T>(items: readonly T[], orderBy: OrderByOption<T> | undefined): T[] {
    if (!orderBy || orderBy.length === 0) return items as T[]
    return [...items].sort(_makeComparator(orderBy))
}

// ── GroupBy / KeyBy Runtime Helpers ──────────────────────────────────────────

type GroupingResult<T> = Map<string | number | boolean, T> | Map<string | number | boolean, T[]>

/** Build the per-item key extractor for a `groupBy` / `keyBy` spec. */
function _buildGroupKeyFn<T>(spec: GroupBySpec<T>): (item: T) => KeyValue {
    if (typeof spec === 'function') return spec
    if (Array.isArray(spec)) {
        if (typeof spec[0] === 'function') return spec[0] as (item: T) => KeyValue
        if (spec.length === 0) throw new Error('Shallow selectors: grouping key list cannot be empty.')
        const keys = spec as readonly string[]
        return (item: T) => _compositeKey(keys, item as Record<string, string | number | undefined>)
    }
    // Single scalar attribute name. `Array.isArray` does not narrow `readonly`
    // arrays out of the union, so re-assert the property-key member here.
    const key = spec as Extract<GroupBySpec<T>, PropertyKey>
    return (item: T) => item[key] as KeyValue
}

/**
 * Stable cache key for a grouping spec. The `kind` tag keeps `groupBy` and
 * `keyBy` entries distinct; function specs key on `_getFnId` (reference
 * identity) unless a `[fn, depKey]` pair supplies an explicit dependency.
 */
function _serializeGroupSpec<T>(kind: 'g' | 'k', spec: GroupBySpec<T>): string {
    if (typeof spec === 'function') return kind + '|fn|' + String(_getFnId(spec))
    if (Array.isArray(spec)) {
        if (typeof spec[0] === 'function') return kind + '|fn|' + String(spec[1])
        return kind + '|c|' + (spec as readonly string[]).join('::')
    }
    return kind + '|s|' + String(spec)
}

/**
 * Group/key a finished result array into a reference-stable `Map`.
 *
 * The `groupingCache` is single-slot per spec: an exact hit (same source array
 * reference) returns the previously-built `Map` unchanged, so consumers using
 * zustand `shallow` (which compares `Map` entries by `Object.is`) short-circuit
 * re-renders. The source-array identity already carries every slice / relation
 * invalidation, so no extra version tracking is needed.
 */
function _applyGrouping<T>(
    array: readonly T[],
    groupBy: GroupBySpec<T> | undefined,
    keyBy: GroupBySpec<T> | undefined,
    groupingCache: FastFIFOCache<{ source: readonly T[]; result: GroupingResult<T> }>
): readonly T[] | GroupingResult<T> {
    if (groupBy === undefined && keyBy === undefined) return array
    if (groupBy !== undefined && keyBy !== undefined) {
        throw new Error('Shallow selectors: `groupBy` and `keyBy` are mutually exclusive — pass only one.')
    }

    const isKeyBy = keyBy !== undefined
    const spec = (isKeyBy ? keyBy : groupBy) as GroupBySpec<T>
    const specKey = _serializeGroupSpec(isKeyBy ? 'k' : 'g', spec)

    const cached = groupingCache.get(specKey)
    if (cached && cached.source === array) return cached.result

    const keyFn = _buildGroupKeyFn(spec)
    let result: GroupingResult<T>
    if (isKeyBy) {
        // `keyBy` values are entity rows straight from the slice, so their
        // identity is already stable across rebuilds — `Object.is` (zustand
        // `shallow`) short-circuits without any reuse step.
        result = _keyBy(array, keyFn)
    } else {
        const fresh = _groupBy(array, keyFn)
        // Fold each freshly-built group array back onto the previous one when
        // shallow-equal, so an unchanged group keeps its reference even though
        // the source array (and hence the Map) had to be rebuilt.
        const prev = cached?.result as Map<string | number | boolean, T[]> | undefined
        if (prev) {
            for (const [k, arr] of fresh) {
                const prevArr = prev.get(k)
                // eslint-disable-next-line @typescript-eslint/no-use-before-define
                if (prevArr) fresh.set(k, _reuseIfShallowEqual(prevArr, arr))
            }
        }
        result = fresh
    }
    groupingCache.set(specKey, { source: array, result })
    return result
}

// ── Factory Types ────────────────────────────────────────────────────────────

type ByIdTable<T> = { byId: Record<number, T>; byUuid?: Record<string, T> }

type SelectByFactoryOptions<T, E extends string> = {
    defaultOrderBy?: OrderByOption<T>
    /**
     * Recursive relations registry for nested-include stitching. The runtime
     * is shape-driven (uses `accessor` + `childRelations`); the type-level
     * include / return shapes flow through `entityName` and the
     * `EntityRelations` interface augmented in the generated selectors file.
     */
    relations?: RelationsConfig
    /**
     * String-literal entity name used to look up `EntityRelations[entityName]`
     * for typed include maps and return types. Generated factory invocations
     * pass this as a literal so the recursive include types stay precise.
     */
    entityName?: E
}

/**
 * One entry in the per-factory result cache. `relationRefs` holds the
 * per-state relation-accessor function identities this result was computed
 * against (sorted by relation name, matching the cache key shape). A cache
 * hit requires byref-equality on every entry so mutating only a sibling
 * slice preserves the stored array reference.
 */
type PerStateChildAccessor = PerStateRelationAccessor
type ResultCacheEntry<T> = {
    result: readonly T[]
    relationRefs: ReadonlyArray<PerStateChildAccessor>
    sliceVersion: number
}

type FactoryCache<T> = {
    byId: Record<number, T>
    byUuid?: Record<string, T>
    items: T[]
    sorted: T[]
    groupCache: Map<string, Map<string | number | boolean, T[]>>
    keyCache: Map<string, Map<string | number | boolean, T>>
    /** Single-slot cache for per-call `orderBy` over the full entity list. */
    lastFullOrderBy: { key: string; result: T[] | undefined }
    version: number
}

// ── Cache-Key Helpers ────────────────────────────────────────────────────────

/**
 * Encode a primitive into a tagged, delimiter-safe string. `|` inside strings
 * is escaped (to `\p`) so the outer container serializer can use `|` as an
 * unambiguous separator, and `\` is doubled so the escape itself round-trips.
 * The tag prefix (`s`/`n`/`b`/`N`/`U`) keeps e.g. the string `"1"` distinct
 * from the number `1` in the resulting key.
 */
function _encodeScalar(v: unknown): string {
    if (v === null) return 'N'
    if (v === undefined) return 'U'
    const t = typeof v
    if (t === 'number') return 'n' + String(v)
    if (t === 'boolean') return 'b' + String(v)

    if (t === 'string') return 's' + (v as string).replace(/\\/g, '\\\\').replace(/\|/g, '\\p')
    return 'x' + String(v)
}

/**
 * Deterministic stringify: object keys sorted recursively, arrays preserved in
 * insertion order (callers pre-sort any positionally-insensitive arrays).
 * Used to build stable cache keys from query shapes. Avoids `JSON.stringify`
 * (banned by the repo's `no-restricted-syntax` rule) and encodes scalars via
 * `_encodeScalar` so embedded `|` or `\` in string values never collide with
 * the container delimiters.
 */
function _stableStringify(value: unknown): string {
    if (value === null || value === undefined || typeof value !== 'object') return _encodeScalar(value)
    if (Array.isArray(value)) {
        let out = '['
        for (let i = 0; i < value.length; i++) {
            if (i > 0) out += '|'
            out += _stableStringify(value[i])
        }
        return out + ']'
    }
    const obj = value as Record<string, unknown>
    const keys = Object.keys(obj)

    let isSorted = true
    for (let i = 1; i < keys.length; i++) {
        if (keys[i - 1]! > keys[i]!) {
            isSorted = false
            break
        }
    }
    const sortedKeys = isSorted ? keys : keys.sort()

    let out = '{'
    let first = true
    for (let i = 0; i < sortedKeys.length; i++) {
        const k = sortedKeys[i]!
        const v = obj[k]
        if (v === undefined) continue
        if (!first) out += '|'
        first = false
        out += _encodeScalar(k) + '=' + _stableStringify(v)
    }
    return out + '}'
}

/** Hard cap on distinct query shapes cached per factory; oldest entry evicted on insertion. */
const RESULT_CACHE_MAX_SIZE = 4096

/**
 * Hard cap on distinct grouping specs cached per factory. Bare function specs
 * serialize via `_getFnId`, so churning inline lambdas would otherwise grow the
 * grouping cache without bound; FIFO eviction keeps it finite.
 */
const GROUPING_CACHE_MAX_SIZE = 256

export class FastFIFOCache<T> {
    private map = new Map<string, T>()
    private keys: string[]
    private head = 0
    private tail = 0
    private size = 0
    private capacity: number

    constructor(capacity: number) {
        this.capacity = capacity
        this.keys = new Array(capacity)
    }

    has(key: string): boolean {
        return this.map.has(key)
    }
    get(key: string): T | undefined {
        return this.map.get(key)
    }
    set(key: string, value: T): this {
        if (!this.map.has(key)) {
            if (this.size === this.capacity) {
                const oldest = this.keys[this.tail]
                if (oldest !== undefined) this.map.delete(oldest)
                this.tail = (this.tail + 1) % this.capacity
            } else {
                this.size++
            }
            this.keys[this.head] = key
            this.head = (this.head + 1) % this.capacity
        }
        this.map.set(key, value)
        return this
    }
}

function _relationRefsMatch(storedRefs: ReadonlyArray<PerStateChildAccessor>, currentRefs: ReadonlyArray<PerStateChildAccessor>): boolean {
    if (storedRefs.length !== currentRefs.length) return false
    for (let i = 0; i < storedRefs.length; i++) {
        if (storedRefs[i] !== currentRefs[i]) return false
    }
    return true
}

/**
 * Reference-stability primitive: returns `prev` whenever it's shallow-equal
 * to `next` (same reference, or two equal-length arrays of byref-equal
 * children), otherwise returns `next`. Lets callers preserve the previously
 * emitted array/object reference across renders so `===` consumers (React,
 * `shallow` zustand subscriber) treat the result as unchanged.
 *
 * Used at every level of the nested-include walk (`_stitchIncludes`) and
 * once more at the top level of `_runByQuery` to fold the freshly-stitched
 * result back onto the cached array reference when nothing changed.
 */
function _reuseIfShallowEqual<T>(prev: T, next: T): T {
    if (prev === next) return prev
    if (!Array.isArray(prev) || !Array.isArray(next) || prev.length !== next.length) return next
    for (let i = 0; i < prev.length; i++) {
        if (prev[i] !== next[i]) return next
    }
    return prev
}

/**
 * OR/AND arm normalizer with an "already canonical" short-circuit. Set
 * semantics: arm order is irrelevant, so we sort by `_stableStringify` to
 * collapse `OR: [a, b]` and `OR: [b, a]` into a single cache entry.
 *
 * Single-pass: compute each arm's normalized form + stable-stringify key
 * once. If every arm is already canonical (no nested `in`/`notIn`/bool-op
 * needed reordering) AND the keys are non-decreasing, the input is
 * canonical → return it unchanged, saving the `out`/`indices` allocations.
 *
 * The cache-miss path runs once per new attrs ref — i.e. on most React
 * renders before the Compiler hoists the attrs — so reusing the original
 * array on the canonical fast path is meaningful at small/medium N.
 */
// `_normalizeForCacheKey` and `_normalizeBoolArms` are mutually recursive;
// JS function declarations are hoisted so the forward references are fine at
// runtime, but eslint's no-use-before-define is order-sensitive.
function _normalizeBoolArms(arms: ReadonlyArray<unknown>): ReadonlyArray<unknown> {
    if (arms.length === 0) return arms
    if (arms.length === 1) {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        const norm = _normalizeForCacheKey(arms[0])
        return norm === arms[0] ? arms : [norm]
    }

    const norms = new Array<unknown>(arms.length)
    const keys = new Array<string>(arms.length)
    let allCanonical = true
    let isSorted = true
    for (let i = 0; i < arms.length; i++) {
        const arm = arms[i]
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        const norm = _normalizeForCacheKey(arm)
        if (norm !== arm) allCanonical = false
        norms[i] = norm
        const key = _stableStringify(norm)
        keys[i] = key
        if (i > 0 && (keys[i - 1] as string) > key) isSorted = false
    }

    if (allCanonical && isSorted) return arms

    const indices = new Array<number>(arms.length)
    for (let i = 0; i < arms.length; i++) indices[i] = i
    indices.sort((a, b) => {
        const ka = keys[a] as string
        const kb = keys[b] as string
        return ka < kb ? -1 : ka > kb ? 1 : 0
    })
    const out = new Array<unknown>(arms.length)
    for (let i = 0; i < arms.length; i++) out[i] = norms[indices[i] as number]
    return out
}

/**
 * Recursive normalizer for cache-key construction: clones any object that
 * embeds a `>1`-element `in`/`notIn` array (set-semantic operators — order is
 * irrelevant) into a sorted-array shape, and rewrites `OR` / `AND` arm
 * arrays into stable-stringify-sorted form (also set-semantic — arm order
 * shouldn't fragment the cache). Recurses into every object child so relation
 * filters whose `any`/`none` predicates contain ComparisonOps and nested
 * boolean arms are normalized in one walk. Returns the original reference
 * when nothing needed normalizing so the no-op path allocates nothing.
 */
function _normalizeForCacheKey(value: unknown): unknown {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) return value
    const o = value as Record<string, unknown>
    let copy: Record<string, unknown> | undefined
    for (const k of Object.keys(o)) {
        const v = o[k]
        let next: unknown
        if ((k === 'in' || k === 'notIn') && Array.isArray(v) && v.length > 1) {
            next = [...(v as readonly (string | number)[])].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0))
        } else if ((k === 'OR' || k === 'AND') && Array.isArray(v)) {
            next = _normalizeBoolArms(v as ReadonlyArray<unknown>)
        } else {
            next = _normalizeForCacheKey(v)
        }
        if (next !== v) {
            if (!copy) copy = { ...o }
            copy[k] = next
        }
    }
    return copy ?? value
}

// ── Filter Pipeline (shared by singular + plural) ────────────────────────────

type RelationFilterParsed = {
    any?: Record<string, unknown>
    none?: Record<string, unknown>
    implicit?: Record<string, unknown>
}
type RelationFilterEntry = readonly [string, RelationFilterParsed, PerStateChildAccessor]

/** Filter applied to an item already narrowed by exact-attr indexes (range ops + relation predicates). */
function _matchesPostIndexFilters<T>(
    item: T,
    rangeAttrs: ReadonlyArray<readonly [string, ComparisonOp]>,
    relationFilters: ReadonlyArray<RelationFilterEntry>
): boolean {
    if (rangeAttrs.length > 0) {
        const obj = item as unknown as Record<string, unknown>
        for (const [attr, op] of rangeAttrs) {
            if (!_matchesComparison(obj[attr] as KeyValue, op)) return false
        }
    }
    for (let i = 0; i < relationFilters.length; i++) {
        const entry = relationFilters[i] as RelationFilterEntry
        const filter = entry[1]
        const accessor = entry[2]
        const childrenRaw = accessor(item as never)
        const children = Array.isArray(childrenRaw) ? childrenRaw : childrenRaw !== null && childrenRaw !== undefined ? [childrenRaw] : []
        if (filter.any !== undefined) {
            const inner = filter.any
            if (!children.some(c => _matchesRelationChild(c, inner))) return false
        }
        if (filter.none !== undefined) {
            const inner = filter.none
            if (children.some(c => _matchesRelationChild(c, inner))) return false
        }
        if (filter.implicit !== undefined) {
            const inner = filter.implicit
            if (!children.some(c => _matchesRelationChild(c, inner))) return false
        }
    }
    return true
}

// ── Atomic Classification + Indexed Filter (shared by atomic + bool-op arms)

type ClassifiedAtomic = {
    exactAttrs: Record<string, string | number | boolean>
    rangeAttrs: Array<[string, ComparisonOp]>
    relationFilters: Array<RelationFilterEntry>
}

/** Bool-op keys are reserved at every recursion depth. Skipped during atomic classification. */
function _isBoolOpKey(k: string): boolean {
    return k === 'OR' || k === 'AND' || k === 'NOT'
}

/**
 * Split a SelectByAttrs object's atomic slice into the three buckets the
 * indexed-filter pipeline consumes. Skips bool-op keys (`OR`/`AND`/`NOT`)
 * and undefined values; resolves each relation predicate's per-state child
 * accessor inline so the second `relations[k]` lookup is dropped.
 */
function _classifyAtomic(attrs: Record<string, unknown>, relations: RelationsConfig | undefined, state: StoreState): ClassifiedAtomic {
    const exactAttrs: Record<string, string | number | boolean> = {}
    const rangeAttrs: Array<[string, ComparisonOp]> = []
    const relationFilters: Array<RelationFilterEntry> = []
    for (const k of Object.keys(attrs)) {
        if (_isBoolOpKey(k)) continue
        const v = attrs[k]
        if (v === undefined) continue
        if (relations && k in relations) {
            if (typeof v === 'object' && v !== null && !Array.isArray(v)) {
                const entry = relations[k]
                if (entry) {
                    const vObj = v as Record<string, unknown>
                    const parsed: RelationFilterParsed = {}
                    if (vObj.any !== undefined) parsed.any = vObj.any as Record<string, unknown>
                    if (vObj.none !== undefined) parsed.none = vObj.none as Record<string, unknown>

                    let hasImplicit = false
                    const implicit: Record<string, unknown> = {}
                    for (const ik of Object.keys(vObj)) {
                        if (ik !== 'any' && ik !== 'none') {
                            implicit[ik] = vObj[ik]
                            hasImplicit = true
                        }
                    }
                    if (hasImplicit || (parsed.any === undefined && parsed.none === undefined)) {
                        parsed.implicit = implicit
                    }

                    relationFilters.push([k, parsed, entry.accessor(state)])
                }
            }
            continue
        }

        if (_isComparisonOp(v)) {
            rangeAttrs.push([k, v])
        } else if (v === null) {
            rangeAttrs.push([k, { isNull: true }])
        } else {
            exactAttrs[k] = v as string | number | boolean
        }
    }
    return { exactAttrs, rangeAttrs, relationFilters }
}

/**
 * Indexed candidate selection + post-index `.filter`, returning a subset of
 * `cache.sorted` (so order is preserved). Used by both the no-bool atomic
 * path and the per-arm computation in the bool-ops path. Singular mode opts
 * into the `keyCache` shortcut for exact-only lookups (returns ≤ 1 item),
 * which bool-op arms cannot use because they always need the full match set.
 */
function _runIndexedFilter<T extends Record<string, unknown>>(cache: FactoryCache<T>, c: ClassifiedAtomic, _mode: 'singular' | 'plural'): readonly T[] {
    const empty: readonly T[] = []
    const exactKeys = Object.keys(c.exactAttrs)
    const hasRange = c.rangeAttrs.length > 0
    const hasRelation = c.relationFilters.length > 0
    // `id`/`uuid` already have dedicated singular fast paths in `_runByQuery`.
    // Every other exact lookup can match multiple rows, so keep the grouped
    // candidate list and let singular callers take the first default-ordered hit.

    let candidates: readonly T[]
    if (exactKeys.length > 0) {
        const keys = exactKeys.length > 1 ? [...exactKeys].sort() : exactKeys
        const shape = keys.join('::')
        const keyFn =
            keys.length === 1
                ? (e: T): KeyValue => (e as Record<string, unknown>)[keys[0] as string] as KeyValue
                : (e: T): KeyValue => _compositeKey(keys, e as Record<string, string | number | undefined>)
        const lookup = keys.length === 1 ? c.exactAttrs[keys[0] as string] : _compositeKey(keys, c.exactAttrs)
        if (lookup === undefined) return empty

        let idx = cache.groupCache.get(shape)
        if (!idx) {
            idx = _groupBy(cache.sorted, keyFn)
            cache.groupCache.set(shape, idx)
        }
        candidates = idx.get(lookup as string | number | boolean) ?? empty
    } else {
        candidates = cache.sorted
    }

    if (hasRange || hasRelation) {
        _metrics.filterCalls++
        return candidates.filter(item => _matchesPostIndexFilters(item, c.rangeAttrs, c.relationFilters))
    }
    return candidates
}

/**
 * Recursive walker that pre-collects every relation accessor referenced by
 * `attrs` (atomic slice + arms / inner predicates inside `OR`/`AND`/`NOT`).
 * The collected refs feed `relationRefs` on the result-cache entry so a
 * mutated relation slice invalidates the entry on the next lookup, just like
 * for the atomic path. Walking is cheap (no row matching, just key iteration)
 * and runs before any computation so cache hits stay zero-allocation.
 */
function _collectRefsRecursive(
    attrs: Record<string, unknown>,
    relations: RelationsConfig | undefined,
    state: StoreState,
    out: Array<readonly [string, PerStateChildAccessor]>
): void {
    if (!relations) return
    for (const k of Object.keys(attrs)) {
        const v = attrs[k]
        if (v === undefined) continue
        if (k === 'OR' || k === 'AND') {
            if (Array.isArray(v)) {
                for (const arm of v) {
                    if (arm && typeof arm === 'object') _collectRefsRecursive(arm as Record<string, unknown>, relations, state, out)
                }
            }
            continue
        }
        if (k === 'NOT') {
            if (typeof v === 'object' && v !== null) _collectRefsRecursive(v as Record<string, unknown>, relations, state, out)
            continue
        }
        if (k in relations && typeof v === 'object' && v !== null && !Array.isArray(v)) {
            const entry = relations[k]
            if (entry) out.push([k, entry.accessor(state)])
        }
    }
}

/**
 * Cheap structural test for "this arm matches every row" — i.e. an arm with
 * no defined keys (`{}` or `{ k: undefined }`). Used as a universal-arm
 * short-circuit in OR/AND so the union/intersection skips arms that would
 * otherwise force `_evalAttrsAsSet` to materialize a copy of `cache.sorted`.
 *
 * Conservative: doesn't recurse into `{ AND: [] }` or `{ OR: [{}] }`. Those
 * are universal too, but rare enough in practice that the recursion cost
 * isn't worth the marginal win here — `_evalAttrsAsSet`'s lazy init already
 * handles them efficiently downstream.
 */
function _isVacuousArm(arm: Record<string, unknown>): boolean {
    for (const k of Object.keys(arm)) {
        if (arm[k] !== undefined) return false
    }
    return true
}

/** True iff the arm has no recursive `OR` / `AND` / `NOT` keys. */
function _armIsPurelyAtomic(arm: Record<string, unknown>): boolean {
    return arm.OR === undefined && arm.AND === undefined && arm.NOT === undefined
}

/**
 * Evaluate `attrs` (atomic + arbitrary-depth bool ops) into a Set of matching
 * rows. Used inside the bool-op branch of `_runByQuery` for both the
 * top-level attrs and recursive arm/inner predicates.
 *
 * Set ops:
 *   - atomic alone        → `Set(_runIndexedFilter(...))`
 *   - `AND: [...]`        → intersect with every arm's set (empty array == identity)
 *   - `OR: []`            → ∅ (vacuously false, short-circuit)
 *   - `OR: [arm, ...]`    → intersect with the union of every arm's set
 *   - `NOT: inner`        → subtract inner's set
 *
 * Lazy-init: `result === null` represents the "universal" candidate set
 * without materializing a copy of `cache.sorted`. The first AND arm or the
 * OR union becomes the result directly when there's no atomic narrowing,
 * which is the common case for the headline composite-key OR pattern. The
 * `cache.sorted` copy only happens when NOT needs a universe to subtract
 * from, or when we exit with no constraints applied at all.
 *
 * Universal-arm short-circuit:
 *   - OR with a vacuous `{}` arm is universal — the whole OR is a no-op
 *     against `result` (intersect-with-universal == identity), so we skip
 *     the union construction entirely.
 *   - AND with a vacuous `{}` arm: that arm contributes nothing
 *     (intersect-with-universal == identity), so we skip it.
 *
 * Inline-atomic-arm: a purely atomic arm in OR writes its candidates
 * straight into the union Set, skipping the intermediate per-arm Set
 * allocation that the recursive call would otherwise produce. Critical for
 * the headline composite-key OR pattern where every arm is atomic.
 *
 * `pipelineRuns` is intentionally NOT incremented here — that counter
 * tracks top-level entries to `_runByQuery`; arms are bookkeeping-free.
 * `filterCalls` may increment per-arm via `_runIndexedFilter`.
 */
function _evalAttrsAsSet<T extends Record<string, unknown>>(
    cache: FactoryCache<T>,
    attrs: Record<string, unknown>,
    relations: RelationsConfig | undefined,
    state: StoreState
): Set<T> {
    const orArms = attrs.OR as ReadonlyArray<Record<string, unknown>> | undefined
    const andArms = attrs.AND as ReadonlyArray<Record<string, unknown>> | undefined
    const notInner = attrs.NOT as Record<string, unknown> | undefined

    const classified = _classifyAtomic(attrs, relations, state)
    const hasAtomic = Object.keys(classified.exactAttrs).length > 0 || classified.rangeAttrs.length > 0 || classified.relationFilters.length > 0

    let result: Set<T> | null = hasAtomic ? new Set<T>(_runIndexedFilter(cache, classified, 'plural')) : null

    if (andArms !== undefined) {
        for (let i = 0; i < andArms.length; i++) {
            const arm = andArms[i] as Record<string, unknown>
            if (_isVacuousArm(arm)) continue
            const armSet = _evalAttrsAsSet(cache, arm, relations, state)
            if (result === null) {
                result = armSet
            } else {
                for (const item of result) {
                    if (!armSet.has(item)) result.delete(item)
                }
                if (result.size === 0) break
            }
        }
    }

    if (orArms !== undefined) {
        if (orArms.length === 0) return new Set()
        let unionIsUniversal = false
        for (let i = 0; i < orArms.length; i++) {
            if (_isVacuousArm(orArms[i] as Record<string, unknown>)) {
                unionIsUniversal = true
                break
            }
        }
        if (!unionIsUniversal) {
            const unionSet = new Set<T>()
            for (let i = 0; i < orArms.length; i++) {
                const arm = orArms[i] as Record<string, unknown>
                if (_armIsPurelyAtomic(arm)) {
                    // Skip the per-arm Set allocation: write candidates straight into the union.
                    // This is the headline-workload hot path (composite-key OR where every arm
                    // is `{ practitioner_id: ..., scheduled_date: ... }` — N atomic arms).
                    const armCls = _classifyAtomic(arm, relations, state)
                    const candidates = _runIndexedFilter(cache, armCls, 'plural')
                    for (let j = 0; j < candidates.length; j++) unionSet.add(candidates[j] as T)
                } else {
                    const armSet = _evalAttrsAsSet(cache, arm, relations, state)
                    for (const item of armSet) unionSet.add(item)
                }
            }
            if (result === null) {
                result = unionSet
            } else {
                for (const item of result) {
                    if (!unionSet.has(item)) result.delete(item)
                }
            }
        }
        // unionIsUniversal: intersect-with-universal == identity, leave result unchanged.
    }

    if (notInner !== undefined) {
        const innerSet = _evalAttrsAsSet(cache, notInner, relations, state)
        if (result === null) {
            // No atomic / AND / OR narrowing yet: subtract from the universe.
            result = new Set<T>(cache.sorted)
        }
        for (const item of innerSet) result.delete(item)
    }

    if (result === null) {
        // No constraints at all: universal predicate.
        result = new Set<T>(cache.sorted)
    }

    return result
}

/** Reserved nested-include keys; never relation names. Skipped during include-tree walks. */
function _isReservedIncludeKey(k: string): boolean {
    return k === 'where' || k === 'orderBy'
}

/** True iff an object has at least one key whose value is not `undefined`. */
function _hasDefinedKeys(obj: Record<string, unknown>): boolean {
    for (const k of Object.keys(obj)) {
        if (obj[k] !== undefined) return true
    }
    return false
}

/**
 * Canonical iteration order over the *relation* keys of an include node:
 * keeps only entries that name a declared relation, drops reserved keys
 * (`where` / `orderBy`) and falsy values (`undefined` / `false`), and sorts
 * the result for stable visit order across caller-side key reorderings.
 *
 * Both `_collectIncludeRefs` (cache-key + cache-invalidation refs) and
 * `_stitchIncludes` (per-row relation projection) walk the same key set;
 * routing both through this helper keeps the filter rules in one place if
 * the reserved set ever grows (e.g. Prisma-style `take` / `skip`).
 */
function _relationIncludeKeys(include: Record<string, unknown>, relations: RelationsConfig): string[] {
    const keys: string[] = []
    for (const k of Object.keys(include)) {
        if (_isReservedIncludeKey(k)) continue
        const v = include[k]
        if (v === undefined || v === false) continue
        if (!(k in relations)) continue
        keys.push(k)
    }
    if (keys.length > 1) keys.sort()
    return keys
}

/**
 * Row-by-row predicate evaluator over the same query language as the top-level
 * `attrs` (atomic + ComparisonOps + relation predicates + `OR`/`AND`/`NOT`).
 * Used to apply nested `where` filters against a relation's resolved children;
 * per-relation row counts are typically small (O(1)–O(100)) so a linear scan
 * outperforms building a transient `FactoryCache` for the indexed-filter
 * machinery.
 */
function _matchesAttrs<T extends Record<string, unknown>>(
    item: T,
    attrs: Record<string, unknown>,
    relations: RelationsConfig | undefined,
    state: StoreState
): boolean {
    const orArms = attrs.OR as ReadonlyArray<Record<string, unknown>> | undefined
    if (orArms !== undefined && !orArms.some(arm => _matchesAttrs(item, arm, relations, state))) return false
    const andArms = attrs.AND as ReadonlyArray<Record<string, unknown>> | undefined
    if (andArms !== undefined && !andArms.every(arm => _matchesAttrs(item, arm, relations, state))) return false
    const notInner = attrs.NOT as Record<string, unknown> | undefined
    if (notInner !== undefined && _matchesAttrs(item, notInner, relations, state)) return false
    const c = _classifyAtomic(attrs, relations, state)
    const obj = item as Record<string, unknown>
    for (const k of Object.keys(c.exactAttrs)) {
        if (obj[k] !== c.exactAttrs[k]) return false
    }
    return _matchesPostIndexFilters(item, c.rangeAttrs, c.relationFilters)
}

/**
 * Apply nested-include `where` (predicate filter) and `orderBy` (sort) to a
 * relation's resolved children. Both are no-ops when undefined / empty / have
 * no defined keys; in that case the input array reference is returned
 * unchanged so reference-stability paths upstream don't allocate.
 */
function _filterAndSortChildren<T extends Record<string, unknown>>(
    children: readonly T[],
    where: Record<string, unknown> | undefined,
    orderBy: OrderByOption<T> | undefined,
    childRelations: RelationsConfig | undefined,
    state: StoreState
): readonly T[] {
    let result: readonly T[] = children
    if (where && _hasDefinedKeys(where)) {
        result = result.filter(item => _matchesAttrs(item, where, childRelations, state))
    }
    if (orderBy && orderBy.length > 0) result = _sortIfNeeded(result, orderBy)
    return result
}

/**
 * Recursively walk the include map and collect every per-state relation
 * accessor in the include tree. The collected accessors feed `relationRefs`
 * so a mutation anywhere in the include tree (top-level OR a deeply-nested
 * child slice) busts the result cache via byref-equality on the next call.
 *
 * Reserved keys (`where` / `orderBy`) are skipped at the relation-key
 * iteration; the `where` sub-tree is descended into via `_collectRefsRecursive`
 * using the CHILD entity's relations config (the predicate filters children),
 * so a mutation of a slice referenced only by a `where` relation predicate
 * also busts the cache.
 *
 * Visit order is name-sorted at every nesting level to make `relationRefs`
 * stable across caller-side include-key reorderings (the test
 * `cache stability across include key reorder` exercises this).
 *
 * Why separate from `_stitchIncludes`? The refs must be known BEFORE the
 * `persistentCache.get(cacheKey)` lookup so a cache-hit on stale state
 * (mutated child slice) is rejected via `_relationRefsMatch`. `_stitchIncludes`
 * runs only on cache-miss, after the result rows are computed. Folding them
 * into a single pass would force stitching unconditionally on every call.
 */
function _collectIncludeRefs(
    include: Record<string, unknown>,
    relations: RelationsConfig | undefined,
    state: StoreState,
    out: Array<PerStateChildAccessor>
): void {
    if (!relations) return
    const keys = _relationIncludeKeys(include, relations)
    for (const k of keys) {
        const entry = relations[k]
        if (!entry) continue
        const v = include[k]
        out.push(entry.accessor(state))
        if (v !== true && typeof v === 'object' && v !== null && !Array.isArray(v)) {
            const sub = v as Record<string, unknown>
            const childRelations = entry.childRelations?.()
            if (childRelations) _collectIncludeRefs(sub, childRelations, state, out)
            const where = sub.where
            if (where && typeof where === 'object' && !Array.isArray(where) && childRelations) {
                const namedWhereRefs: Array<readonly [string, PerStateChildAccessor]> = []
                _collectRefsRecursive(where as Record<string, unknown>, childRelations, state, namedWhereRefs)
                for (const t of namedWhereRefs) out.push(t[1])
            }
        }
    }
}

/**
 * Stitch a recursive include tree onto `rows`. For each `(relationName, sub)`
 * entry in the include map: invoke the per-state accessor on every row to get
 * its child value (array or singleton), recurse into the child entity's
 * relations when `sub` is an object, then fold the stitched relations back
 * onto a copy of the row. `__base__` (non-enumerable) preserves the original
 * row identity so subsequent calls can detect "same upstream row, same
 * stitched children" and reuse the previously-emitted wrapper object.
 *
 * Reference reuse: when a relation array's child references all match the
 * previous result's children (per-row, per-column), the previous array is
 * reused via `prevRelVal`. When every relation column reuses its previous
 * value AND the underlying `__base__` row is unchanged, the previous wrapper
 * itself is reused.
 *
 * `existingRows` is the previous successful result for this exact cache key;
 * `existingMatch` indicates whether `existingRows.length === rows.length`
 * (the stitch is row-aligned, so length mismatch forces fresh wrapping).
 */
function _stitchIncludes(
    rows: readonly Record<string, unknown>[],
    include: Record<string, unknown>,
    relations: RelationsConfig | undefined,
    state: StoreState,
    existingRows: readonly Record<string, unknown>[],
    existingMatch: boolean
): readonly Record<string, unknown>[] {
    if (!relations || rows.length === 0) return rows
    const includeKeys = _relationIncludeKeys(include, relations)
    if (includeKeys.length === 0) return rows

    const stitched = new Array<Record<string, unknown>>(rows.length)
    for (let i = 0; i < rows.length; i++) {
        const item = rows[i] as Record<string, unknown>
        const oldItem = existingMatch ? (existingRows[i] as Record<string, unknown> | undefined) : undefined
        let needsNewObj = oldItem === undefined || item !== oldItem.__base__
        const newRels: Record<string, unknown> = {}

        for (let j = 0; j < includeKeys.length; j++) {
            const k = includeKeys[j] as string
            const sub = include[k]
            const entry = relations[k]
            if (!entry) continue
            const accessor = entry.accessor(state)
            const relValRaw = accessor(item as never)
            const isNested = sub !== true && typeof sub === 'object' && sub !== null && !Array.isArray(sub)
            const subInclude = isNested ? (sub as Record<string, unknown>) : undefined
            const childRelations = isNested ? entry.childRelations?.() : undefined

            // Apply nested `where` / `orderBy` (if any) before recursing into
            // grand-children. For hasOne relations the singleton is wrapped
            // in a one-element array, filtered, then unwrapped — Prisma
            // semantics: a non-matching predicate yields `undefined`.
            const where = subInclude?.where as Record<string, unknown> | undefined
            const orderBy = subInclude?.orderBy as OrderByOption<Record<string, unknown>> | undefined
            const hasFilterOrSort = (where && _hasDefinedKeys(where)) || (orderBy && orderBy.length > 0)

            let relVal: unknown = relValRaw
            if (hasFilterOrSort) {
                if (Array.isArray(relValRaw)) {
                    relVal = _filterAndSortChildren(relValRaw as readonly Record<string, unknown>[], where, orderBy, childRelations, state)
                } else if (relValRaw !== null && relValRaw !== undefined && typeof relValRaw === 'object') {
                    const filtered = _filterAndSortChildren([relValRaw as Record<string, unknown>], where, orderBy, childRelations, state)
                    relVal = filtered[0]
                }
            }

            let stitchedRel: unknown = relVal
            if (isNested && childRelations && subInclude) {
                if (Array.isArray(relVal)) {
                    const prevRelVal = oldItem?.[k]
                    const prevArr = Array.isArray(prevRelVal) ? (prevRelVal as readonly Record<string, unknown>[]) : []
                    const prevMatch = prevArr.length === relVal.length
                    stitchedRel = _stitchIncludes(relVal as readonly Record<string, unknown>[], subInclude, childRelations, state, prevArr, prevMatch)
                } else if (relVal !== null && relVal !== undefined && typeof relVal === 'object') {
                    const prevSingle = oldItem?.[k]
                    const prevArr = prevSingle && typeof prevSingle === 'object' ? [prevSingle as Record<string, unknown>] : []
                    const single = _stitchIncludes([relVal as Record<string, unknown>], subInclude, childRelations, state, prevArr, prevArr.length === 1)
                    stitchedRel = single[0]
                }
            }

            const prevRelVal = oldItem?.[k]
            const finalRel = _reuseIfShallowEqual(prevRelVal, stitchedRel)

            newRels[k] = finalRel
            if (oldItem && finalRel !== prevRelVal) {
                needsNewObj = true
            }
        }

        if (needsNewObj || oldItem === undefined) {
            const copy = { ...item, ...newRels }
            Object.defineProperty(copy, '__base__', { value: item, enumerable: false })
            stitched[i] = copy
        } else {
            stitched[i] = oldItem
        }
    }

    return stitched
}

/**
 * Core attr-driven query pipeline. Returns the filtered, optionally per-call-
 * sorted candidate set. Caller picks `[0]` for singular semantics.
 *
 * Pipeline (the `resultCache`-aware path):
 *   1. peel boolean ops – split off `OR`/`AND`/`NOT` so the rest of the
 *                  pipeline can treat the remainder as the "atomic" slice.
 *   2. classify  – split atomic attrs into `exact`, `range`, `relation`
 *                  buckets via `_classifyAtomic`, resolving the per-state
 *                  relation accessor inline.
 *   3. fast paths – id-only / uuid-only singular queries short-circuit on
 *                  `byId` / `byUuid` and bypass the result cache entirely
 *                  (already O(1), no filter step to memoize). Disabled when
 *                  bool ops are present.
 *   4. cache key – stable serialization of `{ mode, exact, range, rel, bool,
 *                  order, inc }`; `_normalizeForCacheKey` sorts `in` /
 *                  `notIn` arrays AND `OR`/`AND` arms (set semantics), and
 *                  recurses into the include map so nested `where`-arm
 *                  ordering and include-key ordering don't fragment the
 *                  cache.
 *   5. ref preflight – walk the include tree (via `_collectIncludeRefs`,
 *                  including each relation node's `where` sub-tree against
 *                  the child entity's relations) and the bool-op subtrees
 *                  to collect every per-state relation accessor so the
 *                  cache-hit check sees the same `relationRefs` array on
 *                  every call. The include-refs walk runs early (before
 *                  classify); the bool-op walk runs after the cache key is
 *                  built. Atomic path inherits its name-sorted order.
 *   6. cache lookup – on `Map.get` hit + relation-accessor byref match, return
 *                  the stored array reference (the result-cache win).
 *   7. fallback compute – atomic-only uses `_runIndexedFilter`; bool ops go
 *                  through `_evalAttrsAsSet` and project back into
 *                  `cache.sorted` order via membership filter.
 *   8. stitch    – when `callOptions.include` is non-empty, run
 *                  `_stitchIncludes` over the filtered/sorted result; the
 *                  refs collected at step 5 fed the cache key so the stitch
 *                  shape and the cached entry agree.
 *   9. store  – record `{ result, relationRefs }` keyed on the cache key.
 *
 * Returns `noKeys` when nothing-defined-after-filtering – callers map this to
 * `undefined` (singular) or the full sorted list (plural empty-attrs path).
 */
function _runByQuery<T extends Record<string, unknown>>(
    cache: FactoryCache<T>,
    persistentCache: FastFIFOCache<ResultCacheEntry<T>>,
    attrs: Record<string, unknown>,
    relations: RelationsConfig | undefined,
    state: StoreState,
    mode: 'singular' | 'plural',
    callOptions: SelectByOptions<T> | undefined,
    precomputedCacheKey?: string,
    onStore?: (entry: ResultCacheEntry<T>, cacheKey: string) => void
): { kind: 'noKeys' } | { kind: 'result'; result: readonly T[] } {
    _metrics.pipelineRuns++

    const orArms = attrs.OR as ReadonlyArray<Record<string, unknown>> | undefined
    const andArms = attrs.AND as ReadonlyArray<Record<string, unknown>> | undefined
    const notInner = attrs.NOT as Record<string, unknown> | undefined
    const hasBool = orArms !== undefined || andArms !== undefined || notInner !== undefined

    // Includes are processed in two passes around the cache lookup:
    //   1. `_collectIncludeRefs` walks the include tree NOW so its per-state
    //      relation accessors feed both the cache key (via `inc: includeMap`)
    //      and the cache-validation `relationRefs`. A nested-slice mutation
    //      busts the cache via byref-equality on those refs.
    //   2. `_stitchIncludes` runs AFTER the filtered/sorted result is built,
    //      since stitching depends on the matched row set.
    const includeMap =
        callOptions?.include && typeof callOptions.include === 'object' && !Array.isArray(callOptions.include)
            ? (callOptions.include as Record<string, unknown>)
            : undefined
    const includeRefs: Array<PerStateChildAccessor> = []
    if (includeMap) _collectIncludeRefs(includeMap, relations, state, includeRefs)

    const { exactAttrs, rangeAttrs, relationFilters } = _classifyAtomic(attrs, relations, state)
    const exactKeys = Object.keys(exactAttrs)
    const hasRange = rangeAttrs.length > 0
    const hasRelation = relationFilters.length > 0
    const hasInclude = includeRefs.length > 0
    const hasAtomic = exactKeys.length > 0 || hasRange || hasRelation
    const hasQueryKeys = hasAtomic || hasBool

    if (!hasQueryKeys && !hasInclude) return { kind: 'noKeys' }
    if (mode === 'singular' && !hasQueryKeys) return { kind: 'noKeys' }

    const empty: readonly T[] = []

    // id-only / uuid-only singular fast paths are O(1) byId/byUuid hits and
    // never touch the post-index filter, so skip the cache overhead. They
    // require a pure exact-attr query — bool ops would change the result set.
    if (!hasBool && !hasRange && !hasRelation && !hasInclude && exactKeys.length === 1 && exactKeys[0] === 'id') {
        const e = cache.byId[exactAttrs.id as number]
        const result = e ? [e] : empty
        onStore?.({ result, relationRefs: [], sliceVersion: cache.version }, '')
        return { kind: 'result', result }
    }
    if (!hasBool && !hasRange && !hasRelation && !hasInclude && exactKeys.length === 1 && exactKeys[0] === 'uuid' && cache.byUuid) {
        const e = cache.byUuid[exactAttrs.uuid as string]
        const result = e ? [e] : empty
        onStore?.({ result, relationRefs: [], sliceVersion: cache.version }, '')
        return { kind: 'result', result }
    }

    // Sort relation filters and range attrs by name so the cache key shape
    // stays order-independent relative to the caller's attrs object iteration.
    if (relationFilters.length > 1) {
        relationFilters.sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))
    }
    if (rangeAttrs.length > 1) {
        rangeAttrs.sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))
    }

    // `Object.fromEntries` reads tuple slots [0]/[1], so the inline accessor in
    // each `relationFilters` tuple ([2]) is silently dropped from the key shape.
    // The include map is normalised by `_normalizeForCacheKey` (recursive
    // sorted-keys), so caller-side reorders (`{ a: true, b: true }` vs
    // `{ b: true, a: true }`) collapse to the same cache entry.
    const cacheKey =
        precomputedCacheKey ??
        mode +
            '|' +
            _stableStringify(
                _normalizeForCacheKey({
                    exact: exactKeys.length ? exactAttrs : undefined,
                    range: rangeAttrs.length ? Object.fromEntries(rangeAttrs) : undefined,
                    rel: relationFilters.length ? Object.fromEntries(relationFilters) : undefined,
                    bool: hasBool ? { OR: orArms, AND: andArms, NOT: notInner } : undefined,
                    order: _serializeOrderBy(callOptions?.orderBy),
                    inc: includeMap,
                })
            )

    // Build the relation-ref array for cache validation. The atomic path used
    // insertion order (relationFilters sorted by name + includeRefs already
    // sorted by `_collectIncludeRefs`); preserve that for compatibility, then
    // for the bool-ops path collect arm refs, dedup, and sort by name so the
    // array is stable across semantically-equal queries with different arm
    // orderings.
    let relationRefs: ReadonlyArray<PerStateChildAccessor>
    if (!hasBool) {
        const refs: PerStateChildAccessor[] = []
        for (const [, , a] of relationFilters) refs.push(a)
        for (const a of includeRefs) refs.push(a)
        relationRefs = refs
    } else {
        const namedRefs: Array<readonly [string, PerStateChildAccessor]> = []
        for (const [name, , a] of relationFilters) namedRefs.push([name, a])
        for (let i = 0; i < includeRefs.length; i++) namedRefs.push(['_inc_' + String(i), includeRefs[i] as PerStateChildAccessor])
        if (orArms !== undefined) for (const arm of orArms) _collectRefsRecursive(arm, relations, state, namedRefs)
        if (andArms !== undefined) for (const arm of andArms) _collectRefsRecursive(arm, relations, state, namedRefs)
        if (notInner !== undefined) _collectRefsRecursive(notInner, relations, state, namedRefs)
        const seen = new Set<PerStateChildAccessor>()
        const deduped: Array<readonly [string, PerStateChildAccessor]> = []
        for (const t of namedRefs) {
            if (!seen.has(t[1])) {
                seen.add(t[1])
                deduped.push(t)
            }
        }
        deduped.sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))
        relationRefs = deduped.map(t => t[1])
    }

    const existing = persistentCache.get(cacheKey)
    if (existing && existing.sliceVersion === cache.version && _relationRefsMatch(existing.relationRefs, relationRefs)) {
        _metrics.cacheHits++
        onStore?.(existing, cacheKey)
        return { kind: 'result', result: existing.result }
    }
    _metrics.cacheMisses++

    let filtered: readonly T[]
    if (!hasBool) {
        // Atomic-only path, with the empty-lookup short-circuit reproduced here so
        // we still cache an empty-array entry under the right key.
        if (exactKeys.length > 0) {
            const keys = exactKeys.length > 1 ? [...exactKeys].sort() : exactKeys
            const lookup = keys.length === 1 ? exactAttrs[keys[0] as string] : _compositeKey(keys, exactAttrs)
            if (lookup === undefined) {
                const entry = { result: empty, relationRefs, sliceVersion: cache.version }
                persistentCache.set(cacheKey, entry)
                onStore?.(entry, cacheKey)
                return { kind: 'result', result: empty }
            }
        }
        filtered = _runIndexedFilter(cache, { exactAttrs, rangeAttrs, relationFilters }, mode)
    } else {
        const matched = _evalAttrsAsSet(cache, attrs, relations, state)
        const matchedSize = matched.size
        if (matchedSize === 0) {
            filtered = empty
        } else if (mode === 'singular') {
            // Singular: bail out on the first match — no need to project the
            // full array. Preserves default ordering (first match wins).
            let first: T | undefined
            const sorted = cache.sorted
            for (let i = 0; i < sorted.length; i++) {
                const item = sorted[i] as T
                if (matched.has(item)) {
                    first = item
                    break
                }
            }
            filtered = first === undefined ? empty : [first]
        } else {
            // Plural projection with count-early-exit: stop scanning once we've
            // collected every matched row. For OR queries whose matches cluster
            // early in `cache.sorted` this skips the trailing scan; for queries
            // whose matches span the whole list it's equivalent to the simple
            // scan but with one extra cheap loop guard.
            const sorted = cache.sorted
            const arr = new Array<T>(matchedSize)
            let count = 0
            for (let i = 0; i < sorted.length && count < matchedSize; i++) {
                const item = sorted[i] as T
                if (matched.has(item)) arr[count++] = item
            }
            filtered = arr
        }
    }

    let result: readonly T[]
    if (mode === 'singular') {
        result = filtered
    } else {
        const orderBy = callOptions?.orderBy
        result = orderBy !== undefined ? _sortIfNeeded(filtered, orderBy) : filtered
    }

    const hadInclude = includeMap !== undefined && includeRefs.length > 0
    if (hadInclude) {
        const existingResult = existing?.result ?? []
        const existingMatch = existing !== undefined && existing.result.length === result.length
        result = _stitchIncludes(
            result as readonly Record<string, unknown>[],
            includeMap as Record<string, unknown>,
            relations,
            state,
            existingResult as readonly Record<string, unknown>[],
            existingMatch
        ) as readonly T[]
    }

    if (existing && !hadInclude) {
        // For non-include cache misses we collapse content-equal results back to
        // the previous array reference (perf + ref-stability for orthogonal
        // queries — see `efficient invalidation: mutating an item in one range
        // query does NOT invalidate a non-overlapping range query`).
        //
        // For include cache misses we skip this collapse: the cache-invalidation
        // contract pinned by `mutating an in-tree relation slice busts the
        // cached result` requires a new top-level reference whenever the
        // include sub-tree was re-walked (even if the row contents end up
        // shallow-equal). Per-row wrappers are still reused inside
        // `_stitchIncludes`, so zustand `shallow` consumers don't re-render.
        result = _reuseIfShallowEqual(existing.result, result)
    }

    const entry = { result, relationRefs, sliceVersion: cache.version }
    persistentCache.set(cacheKey, entry)
    onStore?.(entry, cacheKey)
    return { kind: 'result', result }
}

function _buildFactoryState<T extends Record<string, unknown>>(
    stateSelector: (state: StoreState) => ByIdTable<T>,
    defaultOrderBy: OrderByOption<T> | undefined
) {
    let sliceVersion = 0
    return createSelector(stateSelector, ({ byId, byUuid }): FactoryCache<T> => {
        _metrics.factoryRebuilds++
        sliceVersion++
        const items = Object.values(byId) as T[]
        const sorted = _sortIfNeeded(items, defaultOrderBy)
        return {
            byId,
            byUuid,
            items,
            sorted,
            groupCache: new Map(),
            keyCache: new Map(),
            lastFullOrderBy: { key: '', result: undefined },
            version: sliceVersion,
        }
    })
}

/** Resolve "no defined attrs" for the plural full-list path, honouring per-call `orderBy`. */
function _resolveFullList<T extends Record<string, unknown>>(
    cache: FactoryCache<T>,
    callOptions: SelectByOptions<T> | undefined,
    defaultKey: string
): readonly T[] {
    const orderBy = callOptions?.orderBy
    if (orderBy === undefined) return cache.sorted
    const key = _serializeOrderBy(orderBy)
    if (key === defaultKey) return cache.sorted
    const slot = cache.lastFullOrderBy
    if (key === slot.key && slot.result) return slot.result
    const result = _sortIfNeeded(cache.items, orderBy)
    slot.key = key
    slot.result = result
    return result
}

// ── Singular Factory ─────────────────────────────────────────────────────────

export interface SelectSingularFn<T extends Record<string, unknown>, E extends string = string> {
    // Mode B (unified query object) is declared before Mode A so that the Mode A
    // signature is the *last* overload — `Parameters<SelectSingularFn>` (used by
    // reselect composition and `Parameters<typeof selectX>` consumers) resolves
    // to the last overload, and Mode A's `(state, attrs, opts?)` shape is the one
    // those call sites expect.
    <Q extends StormQuerySingular<T, E>>(state: StoreState, query: Q): WithRelations<T, E, IncludeOf<Q>> | undefined
    <Opts extends SelectByOptions<T, E>>(
        state: StoreState,
        attrs: SelectByAttrs<T, RelationsAsReturnTypes<E>>,
        opts?: Opts
    ): WithRelations<T, E, IncludeOf<Opts>> | undefined
}

/**
 * Factory for `select<E>` – exact/range/relation lookup that returns a single entity
 * or `undefined`. `attrs` is required at the type level; an all-undefined `attrs`
 * returns `undefined`.
 */
export function _createSelectSingular<T extends Record<string, unknown>, const E extends string = string>(
    stateSelector: (state: StoreState) => ByIdTable<T>,
    factoryOpts?: SelectByFactoryOptions<T, E>
): SelectSingularFn<T, E> {
    const relations = factoryOpts?.relations
    const buildState = _buildFactoryState(stateSelector, factoryOpts?.defaultOrderBy)
    const persistentCache = new FastFIFOCache<ResultCacheEntry<T>>(RESULT_CACHE_MAX_SIZE)
    const weakCache = new WeakMap<Record<string, unknown>, { opts: SelectByOptions<T, E> | undefined; cacheKey: string; entry: ResultCacheEntry<T> }>()

    return ((state, arg1, arg2) => {
        // The signature requires `attrs`, but defend against runtime callers that bypass the type system.
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition, eqeqeq
        if (arg1 == null) return undefined
        const { attrs, opts: callOptions } = _normalizeStormArgs<T, E>(arg1 as Record<string, unknown>, arg2 as SelectByPluralOptions<T, E> | undefined)
        const cache = buildState(state)
        const attrsObj = attrs
        const weakCacheKey = (Object.keys(attrsObj).length > 0 ? attrsObj : arg1) as Record<string, unknown>

        const existingWeak = weakCache.get(weakCacheKey)
        if (
            existingWeak &&
            existingWeak.opts === callOptions &&
            existingWeak.entry.sliceVersion === cache.version &&
            existingWeak.entry.relationRefs.length === 0
        ) {
            _metrics.cacheHits++
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return existingWeak.entry.result[0] as any
        }

        const out = _runByQuery(
            cache,
            persistentCache,
            attrsObj,
            relations,
            state,
            'singular',
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            callOptions as any,
            existingWeak && existingWeak.opts === callOptions ? existingWeak.cacheKey : undefined,
            (entry, cacheKey) => {
                weakCache.set(weakCacheKey, { opts: callOptions, cacheKey, entry })
            }
        )
        if (out.kind === 'noKeys') return undefined
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return out.result[0] as any
    }) as SelectSingularFn<T, E>
}

// ── Plural Factory ───────────────────────────────────────────────────────────

export interface SelectPluralFn<T extends Record<string, unknown>, E extends string = string> {
    (state: StoreState): PluralResult<WithRelations<T, E, true>, Record<string, never>>
    // Mode B (unified query object) is declared before Mode A so that the Mode A
    // signature is the *last* multi-arg overload — `Parameters<SelectPluralFn>`
    // (used by reselect composition and `Parameters<typeof selectX>` consumers)
    // resolves to it, keeping `attrs` / `opts` optional so the selector stays
    // callable with `state` alone.
    <Q extends StormQuery<T, E>>(state: StoreState, query: Q): PluralResult<WithRelations<T, E, IncludeOf<Q>>, Q>
    <Opts extends SelectByPluralOptions<T, E>>(
        state: StoreState,
        attrs?: SelectByAttrs<T, RelationsAsReturnTypes<E>>,
        opts?: Opts
    ): PluralResult<WithRelations<T, E, IncludeOf<Opts>>, Opts>
}

/**
 * Factory for `select<Es>` – exact/range/relation lookup that returns an array.
 * When `attrs` is omitted or has no defined keys, returns the full default-ordered
 * list (cached, reference-stable across calls when `byId` is unchanged).
 *
 * A per-call `orderBy` may be passed via the third `opts` argument; passing the
 * same `orderBy` as `defaultOrderBy` reuses the cached `sorted` reference.
 */
export function _createSelectPlural<T extends Record<string, unknown>, const E extends string = string>(
    stateSelector: (state: StoreState) => ByIdTable<T>,
    factoryOpts?: SelectByFactoryOptions<T, E>
): SelectPluralFn<T, E> {
    const defaultOrderBy = factoryOpts?.defaultOrderBy
    const defaultKey = _serializeOrderBy(defaultOrderBy)
    const relations = factoryOpts?.relations
    const buildState = _buildFactoryState(stateSelector, defaultOrderBy)
    const persistentCache = new FastFIFOCache<ResultCacheEntry<T>>(RESULT_CACHE_MAX_SIZE)
    const weakCache = new WeakMap<Record<string, unknown>, { opts: SelectByPluralOptions<T, E> | undefined; cacheKey: string; entry: ResultCacheEntry<T> }>()
    const groupingCache = new FastFIFOCache<{ source: readonly T[]; result: GroupingResult<T> }>(GROUPING_CACHE_MAX_SIZE)

    return ((state, arg1, arg2) => {
        const { attrs, opts: callOptions } = _normalizeStormArgs<T, E>(
            arg1 as Record<string, unknown> | undefined,
            arg2 as SelectByPluralOptions<T, E> | undefined
        )
        const cache = buildState(state)
        const hasInclude = callOptions?.include && Object.keys(callOptions.include).length > 0
        const attrsObj = attrs
        const hasFilterKeys = Object.keys(attrsObj).length > 0
        // Key on normalized attrs when present so Mode A / Mode B share the same slot; fall back to
        // the Mode B query object for include/orderBy-only calls (empty attrs).
        const weakCacheKey = (hasFilterKeys ? attrsObj : (arg1 ?? attrsObj)) as Record<string, unknown>

        let array: readonly T[]
        if (!hasFilterKeys && !hasInclude) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            array = _resolveFullList(cache, callOptions as any, defaultKey)
        } else {
            const existingWeak = weakCache.get(weakCacheKey)
            if (
                existingWeak &&
                existingWeak.opts === callOptions &&
                existingWeak.entry.sliceVersion === cache.version &&
                existingWeak.entry.relationRefs.length === 0
            ) {
                _metrics.cacheHits++
                array = existingWeak.entry.result
            } else {
                const out = _runByQuery(
                    cache,
                    persistentCache,
                    attrsObj,
                    relations,
                    state,
                    'plural',
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    callOptions as any,
                    existingWeak && existingWeak.opts === callOptions ? existingWeak.cacheKey : undefined,
                    (entry, cacheKey) => {
                        weakCache.set(weakCacheKey, { opts: callOptions, cacheKey, entry })
                    }
                )
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                array = out.kind === 'noKeys' ? _resolveFullList(cache, callOptions as any, defaultKey) : out.result
            }
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return _applyGrouping(array, callOptions?.groupBy, callOptions?.keyBy, groupingCache) as any
    }) as SelectPluralFn<T, E>
}

// ── Hook Signatures ──────────────────────────────────────────────────────────

/**
 * Call signature of a generated `use<Entity>` hook. Mirrors the Mode A /
 * Mode B overloads of {@link SelectSingularFn}, minus the leading `state`
 * argument (the hook reads the store itself). Factored out so the generated
 * file can emit a single `export const useX = _createUseSingular(selectX)`
 * line per entity instead of repeating the full overload set inline.
 */
export interface UseSingularFn<T extends Record<string, unknown>, E extends string = string> {
    <O extends SelectByOptions<T, E>>(attrs: SelectByAttrs<T, RelationsAsReturnTypes<E>>, opts?: O): WithRelations<T, E, IncludeOf<O>> | undefined
    <Q extends StormQuerySingular<T, E>>(query: Q): WithRelations<T, E, IncludeOf<Q>> | undefined
}

/**
 * Call signature of a generated `use<Entities>` hook. Mirrors the no-arg /
 * Mode A / Mode B overloads of {@link SelectPluralFn}, minus the leading
 * `state` argument. See {@link UseSingularFn}.
 */
export interface UsePluralFn<T extends Record<string, unknown>, E extends string = string> {
    (): PluralResult<WithRelations<T, E, true>, Record<string, never>>
    <Q extends StormQuery<T, E>>(query: Q): PluralResult<WithRelations<T, E, IncludeOf<Q>>, Q>
    <O extends SelectByPluralOptions<T, E>>(attrs?: SelectByAttrs<T, RelationsAsReturnTypes<E>>, opts?: O): PluralResult<WithRelations<T, E, IncludeOf<O>>, O>
}
